const CANT_DROP_MARGIN = {
  canDropOnMargin: false
}

/**
 * Calculates position for dropped item relative to target
 * @param {number} targetPosition - Position of the target code
 * @param {number} draggedPosition - Position of the dragged code  
 * @param {string} targetParentId - Parent ID of target code
 * @param {string} draggedParentId - Parent ID of dragged code
 * @returns {number} New position for the dropped item
 */
function getPositionUnder(targetPosition, draggedPosition, targetParentId, draggedParentId) {
  // Convert positions to integers in case they are strings
  const targetPos = parseInt(targetPosition, 10);
  const draggedPos = parseInt(draggedPosition, 10);
  
  // If different parents, just go after target
  if ((targetParentId || null) !== (draggedParentId || null)) {
    return targetPos + 1;
  }
  
  // Same parent - account for current positions
  if (targetPos < draggedPos) {
    return targetPos + 1;
  } else {
    return targetPos;
  }
}

/**
 * Single depth margin position calculation
 * @param {Object} targetCode - Target code properties
 * @param {Object} draggedItem - Dragged item properties
 * @returns {Object} Margin drop rules
 */
function getSingleDepthMarginPosition(targetCode, draggedItem) {
  const { dropCodeId: targetId, level, hasChildren, isLastInSiblings, parent_id: parentId, 
          grandparent_id, position, parentPosition, codeCollapsed } = targetCode;
  const { codeID: dragCodeID } = draggedItem;

  // Dropping on different code
  if (dragCodeID !== targetId) {
    if (!hasChildren || !!codeCollapsed) {
      // Drop as sibling at same level
      return {
        marginDepth: level,
        marginDropCodeId: parentId,
        marginDropPosition: getPositionUnder(position, draggedItem.position, parentId, draggedItem.parent_id),
        canDropOnMargin: true
      }
    } else {
      // Drop as first child
      return {
        marginDepth: level + 1,
        marginDropCodeId: targetId,
        marginDropPosition: 1,
        canDropOnMargin: true
      }
    }
  } else {
    // Dropping on self
    if (hasChildren) {
      throw new Error('A nest of depth one cannot have a child...')
    } else {
      if (!isLastInSiblings) {
        return {
          ...CANT_DROP_MARGIN,
          level: level + 1
        };
      } else if (parentId === null) {
        // At root level
        return {
          ...CANT_DROP_MARGIN,
          level: level + 1
        };
      } else {
        // Promote up one level
        return {
          marginDepth: level - 1,
          marginDropCodeId: grandparent_id,
          marginDropPosition: getPositionUnder(parentPosition, position, grandparent_id, parentId),
          canDropOnMargin: true
        }
      }
    }
  }
}

/**
 * Double depth margin position calculation
 * @param {Object} targetCode - Target code properties
 * @param {Object} draggedItem - Dragged item properties
 * @returns {Object} Margin drop rules
 */
function getDoubleDepthMarginPosition(targetCode, draggedItem) {
  const { dropCodeId: targetId, level, hasChildren, isLastInSiblings, isParentLastInSiblings,
          parent_id: parentId, grandparent_id, position, parentPosition, codeCollapsed, grandparentPosition } = targetCode;
  const { codeID: dragCodeID } = draggedItem;

  if (targetId === dragCodeID) {
    return CANT_DROP_MARGIN;
  } else if (parentId === dragCodeID) {
    // Parent is being dragged
    if (!isLastInSiblings || !isParentLastInSiblings) {
      return {
        ...CANT_DROP_MARGIN,
        level: level + 1
      }
    } else {
      if (grandparent_id === null) {
        // Grandparent is root
        return {
          ...CANT_DROP_MARGIN,
          level: level + 1
        };
      } else {
        return {
          marginDepth: 1,
          marginDropCodeId: null,
          marginDropPosition: getPositionUnder(grandparentPosition, draggedItem.position, null, draggedItem.parent_id),
          canDropOnMargin: true
        }
      }
    }
  } else {
    // Normal drop scenarios
    if (level === 1) {
      if (!hasChildren || !!codeCollapsed) {
        return {
          marginDepth: level,
          marginDropCodeId: parentId,
          marginDropPosition: getPositionUnder(position, draggedItem.position, parentId, draggedItem.parent_id),
          canDropOnMargin: true
        }
      } else {
        return {
          marginDepth: level + 1,
          marginDropCodeId: targetId,
          marginDropPosition: 1,
          canDropOnMargin: true
        }
      }
    } else if (level === 2) {
      if (!hasChildren || !!codeCollapsed) {
        return {
          marginDepth: level,
          marginDropCodeId: parentId,
          marginDropPosition: getPositionUnder(position, draggedItem.position, parentId, draggedItem.parent_id),
          canDropOnMargin: true
        }
      } else {
        return {
          ...CANT_DROP_MARGIN,
          level: level + 1
        };
      }
    } else {
      // Level 3
      if (hasChildren) {
        throw new Error('level 3 should not be allowed to have children')
      } else {
        if (isLastInSiblings) {
          return {
            marginDepth: level - 1,
            marginDropCodeId: grandparent_id,
            marginDropPosition: getPositionUnder(parentPosition, draggedItem.position, grandparent_id, draggedItem.parent_id),
            canDropOnMargin: true
          }
        } else {
          return {
            ...CANT_DROP_MARGIN,
            marginDepth: level + 1
          }
        }
      }
    }
  }
}

/**
 * Triple depth margin position calculation
 * @param {Object} targetCode - Target code properties
 * @param {Object} draggedItem - Dragged item properties
 * @returns {Object} Margin drop rules
 */
function getTripleDepthMarginPosition(targetCode, draggedItem) {
  const { dropCodeId: targetId, level, hasChildren, isLastInSiblings, isParentLastInSiblings,
          parent_id: parentId, grandparent_id, position, parentPosition, 
          grandparentPosition, codeCollapsed } = targetCode;
  const { codeID: dragCodeID } = draggedItem;

  // No nesting on yourself, parent, or grandparent if you are the third layer
  if (parentId === dragCodeID || grandparent_id === dragCodeID || targetId === dragCodeID) {
    return CANT_DROP_MARGIN;
  }
  
  // Can't drop if target has expanded children
  if (hasChildren && !codeCollapsed) {
    return CANT_DROP_MARGIN;
  }
  
  if (level === 1) {
    return {
      marginDepth: 1,
      marginDropCodeId: null,
      marginDropPosition: getPositionUnder(position, draggedItem.position, null, draggedItem.parent_id),
      canDropOnMargin: true
    }
  } else if (level === 2) {
    if (!isLastInSiblings) {
      return CANT_DROP_MARGIN;
    }

    return {
      marginDepth: 1,
      marginDropCodeId: null,
      marginDropPosition: getPositionUnder(parentPosition, draggedItem.position, null, draggedItem.parent_id),
      canDropOnMargin: true
    }
  } else if (level === 3) {
    if (!isLastInSiblings || !isParentLastInSiblings) {
      return {
        ...CANT_DROP_MARGIN,
        marginDepth: level
      }
    }

    return {
      marginDepth: 1,
      marginDropCodeId: null,
      marginDropPosition: getPositionUnder(grandparentPosition, draggedItem.position, null, draggedItem.parent_id),
      canDropOnMargin: true
    }
  }
}

/**
 * Checks if the move would be a no-op (same position)
 * @param {Object} draggedItem - Dragged item properties
 * @param {Object} returnCode - Calculated margin rules
 * @returns {Object} No-op flag if applicable
 */
function isNoOp(draggedItem, returnCode) {
  if (!returnCode.canDropOnMargin || !draggedItem.codeID || !returnCode) return {};
  
  if ((draggedItem.parent_id || null) !== (returnCode.marginDropCodeId || null)) return {};
  if (draggedItem.position !== returnCode.marginDropPosition) return {};

  return {
    marginNoOp: true
  }
}

/**
 * Main function to calculate margin position rules
 * @param {Object} targetCode - Target code properties with pre-computed flags
 * @param {Object} draggedItem - Dragged item with maxDepth/dragCodeDepth
 * @returns {Object} Complete margin drop rules
 */
export function calculateMarginPosition(targetCode, draggedItem) {
  if (!targetCode.dropCodeId || !draggedItem.codeID) return CANT_DROP_MARGIN;

  const dragCodeDepth = draggedItem.maxDepth || 0;
  let returnCode;
  if (dragCodeDepth === 0) {
    returnCode = getSingleDepthMarginPosition(targetCode, draggedItem);
  } else if (dragCodeDepth === 1) {
    returnCode = getDoubleDepthMarginPosition(targetCode, draggedItem);
  } else {
    returnCode = getTripleDepthMarginPosition(targetCode, draggedItem);
  }

  return {
    ...returnCode,
    ...isNoOp(draggedItem, returnCode)
  };
}